/**
 * DocumentEditor
 *
 * Rich text/markdown editor for document artifacts
 *
 * ACCESS CONTROL RULES (Not yet implemented, but documented for future implementation):
 * =====================================================================================
 *
 * Edit Permissions:
 * - Users can ONLY edit artifacts they own (access_level === 'owner' or undefined)
 * - Users can ONLY edit artifacts shared with them with "edit" permission (access_level === 'edit')
 * - Users CANNOT edit artifacts shared with "view" permission only (access_level === 'view')
 *
 * Read/View Permissions:
 * - Users can read/view artifacts they own
 * - Users can read/view artifacts shared with them (both "edit" and "view" permissions)
 *
 * Future Implementation:
 * - Check artifact.access_level before allowing edits
 * - Disable editor and show read-only mode when access_level === 'view'
 * - Show visual indicator (e.g., "Read-only" badge) for view-only artifacts
 * - Prevent title changes, status changes, and content edits for view-only access
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DocumentArtifact } from '../../artifacts';
import { useWorkspace } from '../../artifacts';
import RichMarkdownEditor from './RichMarkdownEditor';
import CuriousTiptapEditor from '../../../Editor/CuriousTiptapEditor';
import { Modal, ModalHeader, ModalBody } from '../../../design-system/Modal';
import { StatusSelector } from '../../../common/StatusSelector';
import { ShareButton } from '../../../common/ShareButton';
import toast from 'react-hot-toast';

interface DocumentEditorProps {
  artifact: DocumentArtifact;
}

export const DocumentEditor: React.FC<DocumentEditorProps> = ({ artifact }) => {
  const { updateArtifact, saveArtifactImmediately, revertArtifact, isSyncing } = useWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightLine, setHighlightLine] = useState<number | undefined>();
  const [highlightEndLine, setHighlightEndLine] = useState<number | undefined>();
  const [highlightText, setHighlightText] = useState<string | undefined>();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [characterCount, setCharacterCount] = useState<{
    characters: number;
    words: number;
  } | null>(null);

  // Reset count when switching to another document so we don't show stale numbers
  useEffect(() => {
    setCharacterCount(null);
  }, [artifact.id, artifact.format]);

  // Read line number and text from URL params
  useEffect(() => {
    const lineParam = searchParams.get('line');
    const endLineParam = searchParams.get('endLine');
    const textParam = searchParams.get('text');
    
    if (lineParam) {
      const lineNum = parseInt(lineParam, 10);
      if (!isNaN(lineNum)) {
        setHighlightLine(lineNum);
      }
    }
    
    if (endLineParam) {
      const endLineNum = parseInt(endLineParam, 10);
      if (!isNaN(endLineNum)) {
        setHighlightEndLine(endLineNum);
      }
    }
    
    if (textParam) {
      // Decode the text (it's URL encoded)
      try {
        setHighlightText(decodeURIComponent(textParam));
      } catch {
        setHighlightText(textParam);
      }
    }
    
    // Clear params after reading
    if (lineParam || endLineParam || textParam) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('line');
        next.delete('endLine');
        next.delete('text');
        return next;
      });
    }
  }, [searchParams, setSearchParams]);

  const handleContentChange = useCallback(
    (content: string | Record<string, unknown>, format: 'markdown' | 'richtext' | 'tiptap_json') => {
      updateArtifact(artifact.id, { content, format });
    },
    [artifact.id, updateArtifact],
  );

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateArtifact(artifact.id, { title: e.target.value });
    },
    [artifact.id, updateArtifact],
  );

  return (
    <div className="flex flex-col h-full items-center">
      <div className="w-full max-w-5xl flex flex-col h-full">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-neutral-100 bg-white">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <input
              type="text"
              value={artifact.title}
              onChange={handleTitleChange}
              className="flex-1 min-w-0 pr-4 text-lg font-medium text-neutral-900 bg-transparent border-none outline-none focus:ring-0 px-0 truncate"
              placeholder="Untitled Document"
            />
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <ShareButton artifactId={artifact.id} />
            <StatusSelector
              currentStatus={artifact.status}
              onStatusChange={async (status) => {
                updateArtifact(artifact.id, { status });
                toast.success('Status updated');
              }}
            />
            <button
              type="button"
              onClick={() => setShowInfoModal(true)}
              className="flex items-center justify-center w-8 h-8 rounded text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 transition-colors"
              title="Workspace Information"
              aria-label="Show workspace information"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => revertArtifact(artifact.id)}
              className="flex items-center justify-center w-8 h-8 rounded text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSyncing(artifact.id)}
              title="Revert to last saved version"
              aria-label="Revert document"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Editor area */}
        <div className="flex-1 min-h-0 overflow-hidden bg-white">
          {artifact.format === 'tiptap_json' && typeof artifact.content === 'object' ? (
            <CuriousTiptapEditor
              key={artifact.id}
              value={artifact.content as Record<string, unknown>}
              onChange={(value) => handleContentChange(value, 'tiptap_json')}
              className="h-full"
              onCharacterCountChange={setCharacterCount}
            />
          ) : (
            <RichMarkdownEditor
              key={artifact.id}
              value={artifact.content}
              format={artifact.format}
              onChange={handleContentChange}
              artifactId={artifact.id}
              saveArtifactImmediately={saveArtifactImmediately}
              highlightLine={highlightLine}
              highlightEndLine={highlightEndLine}
              highlightText={highlightText}
              onCharacterCountChange={setCharacterCount}
            />
          )}
        </div>

        {/* Footer with metadata */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-neutral-100 bg-neutral-50 text-xs text-neutral-500">
          <span>
            Created by {artifact.createdBy} · {new Date(artifact.createdAt).toLocaleDateString()}
            {characterCount != null && (
              <> · {characterCount.characters} characters · {characterCount.words} words</>
            )}
          </span>
        </div>
      </div>

      {/* Workspace Information Modal */}
      <Modal isOpen={showInfoModal} onClose={() => setShowInfoModal(false)} size="lg">
        <ModalHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-neutral-900">How the Workspace Works</h2>
            <button
              onClick={() => setShowInfoModal(false)}
              className="text-neutral-400 hover:text-neutral-600 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </ModalHeader>
        <ModalBody className="rounded-b-xl sm:rounded-b-2xl">
          <div className="py-4 space-y-6 text-sm text-neutral-700">
            <section>
              <h3 className="text-base font-semibold text-neutral-900 mb-2">Auto-Save</h3>
              <p className="text-neutral-700 leading-relaxed">
                Your changes are automatically saved when you press <kbd className="px-1.5 py-0.5 bg-neutral-100 border border-neutral-300 rounded text-xs font-mono">Enter</kbd> to create a new line or when you click away from the document. No need to manually save—your work is always protected.
              </p>
            </section>

            <section>
              <h3 className="text-base font-semibold text-neutral-900 mb-2">Sensitive Data Protection</h3>
              <p className="text-neutral-700 leading-relaxed mb-2">
                The workspace automatically detects and protects sensitive information like emails, phone numbers, IBANs, and credit cards. Protected data is highlighted with a yellow background and lock icon.
              </p>
              <ul className="list-disc list-inside space-y-1 text-neutral-600 ml-2">
                <li>Right-click selected text to manually mark it as sensitive</li>
                <li>Protected data is encoded before storage for GDPR compliance</li>
                <li>You always see the original text—codes are only used internally</li>
              </ul>
            </section>

            <section>
              <h3 className="text-base font-semibold text-neutral-900 mb-2">Document Versions</h3>
              <p className="text-neutral-700 leading-relaxed">
                Every change creates a new version. Use the <strong>Revert</strong> button (↶ icon) to restore your document to the last saved version if you need to undo recent changes.
              </p>
            </section>

            <section>
              <h3 className="text-base font-semibold text-neutral-900 mb-2">Rich Text Editing</h3>
              <p className="text-neutral-700 leading-relaxed mb-2">
                Use the toolbar to format your text with headings, bold, italic, lists, and more. The editor supports Markdown syntax:
              </p>
              <ul className="list-disc list-inside space-y-1 text-neutral-600 ml-2">
                <li><code className="px-1 py-0.5 bg-neutral-100 rounded text-xs"># Heading</code> for headings</li>
                <li><code className="px-1 py-0.5 bg-neutral-100 rounded text-xs">**bold**</code> and <code className="px-1 py-0.5 bg-neutral-100 rounded text-xs">*italic*</code> for emphasis</li>
                <li><code className="px-1 py-0.5 bg-neutral-100 rounded text-xs">-</code> or <code className="px-1 py-0.5 bg-neutral-100 rounded text-xs">1.</code> for lists</li>
                <li><code className="px-1 py-0.5 bg-neutral-100 rounded text-xs">@</code> for mentions</li>
              </ul>
            </section>

            <section>
              <h3 className="text-base font-semibold text-neutral-900 mb-2">Multiple Documents</h3>
              <p className="text-neutral-700 leading-relaxed">
                Switch between open documents using the tabs at the top. Click the <strong>+</strong> button to create a new document. Each document is independent and auto-saves separately.
              </p>
            </section>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default DocumentEditor;
